import React, { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import NotFound from "../ul/NotFound";

const PlayAnalyticsChart = ({ playData = [] }) => {
  // Category mapping rules
  const GAME_MAP = {
    Game: ["Game_Scratch", "Game_Captcha"],
    Share: ["telegram", "whatsapp", "review_coin", "referral_coin"],
    "Pub Game": ["Pub_Game"],
    "Web Game": ["game"],
    "Web Quiz": ["quiz"],
  };

  const getCategory = (name) => {
    for (const key of Object.keys(GAME_MAP)) {
      if (GAME_MAP[key].includes(name)) return key;
    }
    return "Web Quiz";
  };

  const [filteredCategory, setFilteredCategory] = useState("");
  const [openCategory, setOpenCategory] = useState(null);

  // Filter dataset according to category
  const filteredData = useMemo(() => {
    if (!filteredCategory) return playData;
    return playData.filter(
      (item) => getCategory(item.game_name) === filteredCategory
    );
  }, [filteredCategory, playData]);

  // Aggregate coins & play count by category
  const aggregated = useMemo(() => {
    const agg = {};
    for (const item of filteredData) {
      const category = getCategory(item.game_name);
      if (!agg[category]) agg[category] = { coins: 0, plays: 0 };
      agg[category].coins += Number(item.coin_added || 0);
      agg[category].plays += 1;
    }
    return agg;
  }, [filteredData]);

  // Detailed grouping inside category
  const detailed = useMemo(() => {
    const details = {};
    for (const item of filteredData) {
      const category = getCategory(item.game_name);
      const game = item.game_name;
      if (!details[category]) details[category] = {};
      if (!details[category][game])
        details[category][game] = { coins: 0, plays: 0 };

      details[category][game].coins += Number(item.coin_added || 0);
      details[category][game].plays += 1;
    }
    return details;
  }, [filteredData]);

  const categories = Object.keys(aggregated);
  const coinsArr = categories.map((key) => aggregated[key].coins);
  const playsArr = categories.map((key) => aggregated[key].plays);

  const hasData = categories.length > 0;

  // Enhanced Chart Options with better styling
  const chartOption = {
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      textStyle: {
        color: "#334155",
        fontSize: 13,
      },
      axisPointer: {
        type: "shadow",
        shadowStyle: {
          color: "rgba(59, 130, 246, 0.1)",
        },
      },
    },
    legend: {
      data: ["Plays", "Coins"],
      bottom: 20,
      itemGap: 30,
      textStyle: {
        fontSize: 14,
        fontWeight: "600",
        color: "#475569",
      },
      icon: "roundRect",
      itemWidth: 30,
      itemHeight: 14,
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "15%",
      top: "5%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLine: {
        lineStyle: {
          color: "#cbd5e1",
          width: 2,
        },
      },
      axisLabel: {
        color: "#64748b",
        fontSize: 12,
        fontWeight: "600",
        interval: 0,
        rotate: categories.length > 4 ? 15 : 0,
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: {
      type: "value",
      axisLine: {
        show: false,
      },
      axisLabel: {
        color: "#64748b",
        fontSize: 12,
        fontWeight: "600",
      },
      splitLine: {
        lineStyle: {
          color: "#f1f5f9",
          width: 1,
          type: "dashed",
        },
      },
    },
    series: [
      {
        name: "Plays",
        type: "bar",
        data: playsArr,
        itemStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "#818cf8" },
              { offset: 1, color: "#6366f1" },
            ],
          },
          borderRadius: [8, 8, 0, 0],
        },
        emphasis: {
          itemStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "#6366f1" },
                { offset: 1, color: "#4f46e5" },
              ],
            },
          },
        },
        barWidth: "35%",
      },
      {
        name: "Coins",
        type: "bar",
        data: coinsArr,
        itemStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "#22d3ee" },
              { offset: 1, color: "#06b6d4" },
            ],
          },
          borderRadius: [8, 8, 0, 0],
        },
        emphasis: {
          itemStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "#06b6d4" },
                { offset: 1, color: "#0891b2" },
              ],
            },
          },
        },
        barWidth: "35%",
      },
    ],
  };

  const categoryOptions = [
    "All",
    "Game",
    "Share",
    "Pub Game",
    "Web Game",
    "Web Quiz",
  ];

  // Category color mapping
  const categoryColors = {
    Game: { from: "from-purple-500", to: "to-purple-700", icon: "🎮" },
    Share: { from: "from-pink-500", to: "to-rose-600", icon: "📤" },
    "Pub Game": { from: "from-orange-500", to: "to-orange-700", icon: "🎯" },
    "Web Game": { from: "from-blue-500", to: "to-blue-700", icon: "🕹️" },
    "Web Quiz": { from: "from-emerald-500", to: "to-teal-600", icon: "❓" },
  };

  return (
    <div className="p-6 md:p-10 rounded-3xl bg-gradient-to-br from-white to-slate-50 shadow-lg">
      <div className="relative z-10">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-6">
          <div className="flex items-start gap-4">
            <div className="w-1 h-7 bg-gradient-to-b from-purple-500 to-amber-500 rounded-full" />
            <div>
              <h3 className="text-lg md:text-xl font-bold text-slate-800">
                Gameplay & Coins
              </h3>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 items-center">
            <select
              value={filteredCategory}
              onChange={(e) =>
                setFilteredCategory(
                  e.target.value === "All" ? "" : e.target.value
                )
              }
              className="px-5 py-3 rounded-xl border-2 border-slate-300 font-bold bg-white shadow-md hover:border-slate-400 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              {categoryOptions.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!hasData ? (
          <div className="flex items-center justify-center h-96 bg-white rounded-2xl">
            <NotFound />
          </div>
        ) : (
          <>
            {/* Chart Section */}
            <div className="bg-white rounded-3xl md:p-8 mb-8">
              <div style={{ height: "480px", width: "100%" }}>
                <ReactECharts option={chartOption} style={{ height: "100%" }} />
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
              {/* Total Plays Card */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                <div className="relative bg-gradient-to-br from-indigo-500 to-indigo-700 p-8 rounded-2xl shadow-2xl transform group-hover:-translate-y-2 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-3xl">
                      🎮
                    </div>
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
                  </div>
                  <p className="text-indigo-100 text-sm font-bold uppercase tracking-wider mb-2">
                    Total Plays
                  </p>
                  <p className="text-white text-xl md:text-2xl font-bold tracking-tight">
                    {playsArr.reduce((a, b) => a + b, 0).toLocaleString()}
                  </p>
                  <div className="mt-4 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white/60 rounded-full w-full animate-pulse"></div>
                  </div>
                </div>
              </div>

              {/* Total Coins Card */}
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                <div className="relative bg-gradient-to-br from-cyan-500 to-cyan-700 p-8 rounded-2xl shadow-2xl transform group-hover:-translate-y-2 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-3xl">
                      🪙
                    </div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse shadow-lg shadow-yellow-400/50"></div>
                  </div>
                  <p className="text-cyan-100 text-sm font-bold uppercase tracking-wider mb-2">
                    Total Coins
                  </p>
                  <p className="text-white text-xl md:text-2xl font-bold font-extrabold tracking-tight">
                    {coinsArr.reduce((a, b) => a + b, 0).toLocaleString()}
                  </p>
                  <div className="mt-4 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white/60 rounded-full w-full animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Category Breakdown Section */}
            <div className="">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-pulse shadow-lg"></div>
                <h4 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-slate-800 to-slate-600">
                  Category Breakdown
                </h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
                {categories.map((category) => {
                  const colors = categoryColors[category] || {
                    from: "from-gray-500",
                    to: "to-gray-700",
                    icon: "📊",
                  };
                  return (
                    <div
                      key={category}
                      onClick={() =>
                        setOpenCategory(
                          openCategory === category ? null : category
                        )
                      }
                      className="group relative cursor-pointer"
                    >
                      {/* Hover glow effect */}
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${colors.from} ${colors.to} rounded-2xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300`}
                      ></div>

                      <div className="relative bg-white rounded-2xl p-6 shadow-lg border-2 border-slate-200 group-hover:border-transparent group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2 group-hover:scale-105">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-12 h-12 bg-gradient-to-br ${colors.from} ${colors.to} rounded-xl flex items-center justify-center text-2xl shadow-lg`}
                            >
                              {colors.icon}
                            </div>
                            <h5 className="font-extrabold text-slate-900 text-lg">
                              {category}
                            </h5>
                          </div>
                          <div
                            className={`transform transition-transform duration-300 ${
                              openCategory === category ? "rotate-180" : ""
                            }`}
                          >
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
                              <span className="text-white text-sm font-bold">
                                ▼
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="space-y-3 mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 bg-indigo-500 rounded-full shadow-sm"></div>
                            <span className="text-slate-600 text-sm font-semibold">
                              Plays:
                            </span>
                            <span className="ml-auto text-lg font-extrabold text-indigo-700">
                              {aggregated[category].plays.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 bg-cyan-500 rounded-full shadow-sm"></div>
                            <span className="text-slate-600 text-sm font-semibold">
                              Coins:
                            </span>
                            <span className="ml-auto text-lg font-extrabold text-cyan-700">
                              {aggregated[category].coins.toLocaleString()}
                            </span>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {openCategory === category && (
                          <div className="animate-in slide-in-from-top duration-300">
                            <div className="h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent mb-4"></div>
                            <div className="space-y-2 max-h-56 overflow-y-auto custom-scrollbar">
                              {Object.keys(detailed[category] || {}).map(
                                (game, idx) => (
                                  <div
                                    key={game}
                                    className="bg-gradient-to-r from-slate-50 to-blue-50 p-3 rounded-xl hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 border border-slate-200 group/item"
                                    style={{
                                      animationDelay: `${idx * 50}ms`,
                                    }}
                                  >
                                    <div className="flex justify-between items-center gap-3">
                                      <span className="font-bold text-slate-800 text-sm truncate flex-1 group-hover/item:text-blue-700 transition-colors">
                                        {game}
                                      </span>
                                      <div className="flex items-center gap-2 text-xs font-extrabold">
                                        <span className="bg-cyan-500 text-white px-2 py-1 rounded-lg shadow-sm">
                                          {detailed[category][
                                            game
                                          ].coins.toLocaleString()}
                                          c
                                        </span>
                                        <span className="bg-indigo-500 text-white px-2 py-1 rounded-lg shadow-sm">
                                          {detailed[category][
                                            game
                                          ].plays.toLocaleString()}
                                          p
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #6366f1, #8b5cf6);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #4f46e5, #7c3aed);
        }
      `}</style>
    </div>
  );
};

export default PlayAnalyticsChart;
